<script setup lang="ts">
import slugify from 'slugify'
import dayjs from 'dayjs'

import useMedia from '@/composables/media'

const props = defineProps<{
  modelValue: string | null
  originalAudioName: string
}>()

const { t } = useI18n()

const awsLink = ref(import.meta.env.VITE_AWS_STORAGE_URL)

const audioSrc = ref('')
audioSrc.value = props.modelValue || `${awsLink.value}/missed-calls/default_voiceMessage.mp3`

const isRecording = ref<boolean>(false)

const hasAudio = ref<boolean>(false)

const audio = ref<Blob>()

const hasBeenUploaded = ref<boolean>(false)
const showRenameFile = ref<boolean>(false)

const handleUploadRecord = (audioUrl, audioBlob) => {
  showRenameFile.value = true
  hasBeenUploaded.value = true
  audio.value = audioBlob

  audioSrc.value = null

  setDefaultFilename()

  hasAudio.value = true
  isRecording.value = false

  saveRecordAsBlob(audioBlob)
  audioSrc.value = audioUrl
}

const setDefaultFilename = () => {
  const date = dayjs().format('DD/MM/YYYY').toString().replaceAll('/', '-')
  recordingName.value = date
}

const { createAudioBlob } = useMedia()

const saveRecordAsBlob = (audioBlob) => {
  const reader = new FileReader()
  const newBlob = createAudioBlob([audioBlob])

  reader.readAsDataURL(newBlob)

  reader.onload = () => {
    return emit('update', reader.result, slugify(recordingName.value, '-'))
  }
}

const defaultRecordingName = t('pages.missedCalls.missedCallsDetailPage.audioRecorder.defaultName')
const recordingName = ref<string>(props.originalAudioName)

const confirmationModalIsOpen = ref(false)

const confirmationModal = computed(() => {
  return {
    title: t('pages.missedCalls.missedCallsDetailPage.audioRecorder.modal.title'),
    text: t('pages.missedCalls.missedCallsDetailPage.audioRecorder.modal.text', {
      recordingName: recordingName.value
    }),
    cancel: t('pages.missedCalls.missedCallsDetailPage.audioRecorder.modal.cancel'),
    confirm: t('pages.missedCalls.missedCallsDetailPage.audioRecorder.modal.confirm')
  }
})

const renameFile = (filename) => {
  if (hasBeenUploaded.value) {
    saveRecordAsBlob(audio.value)
  } else {
    emit('renameFile', filename)
  }
}

const deleteRecord = () => {
  showRenameFile.value = false
  hasBeenUploaded.value = true
  confirmationModalIsOpen.value = false
  recordingName.value = defaultRecordingName
  audioSrc.value = null

  audioSrc.value = `${awsLink.value}/missed-calls/default_voiceMessage.mp3`

  emit('reset', slugify(recordingName.value, '-'))
}

const emit = defineEmits<{
  (e: 'update', file: string | ArrayBuffer, name: string): void
  (e: 'renameFile', filename: string): void
  (e: 'reset', name: string): void
}>()
</script>

<template>
  <div class="sm:col-span-6">
    <div class="mb-5 border-b pb-5">
      <p class="mb-4 py-2 font-medium text-foreground">
        {{ t('pages.missedCalls.missedCallsDetailPage.audioRecorder.myMessage') }}
      </p>
      <AppSoundPlayer
        v-model="recordingName"
        :audio="audioSrc"
        :default-recording-name="defaultRecordingName"
        :show-rename-file="showRenameFile"
        @delete="confirmationModalIsOpen = true"
        @rename-file="renameFile"
      />
      <BaseModal
        icon="HeroPhoneIconSolid"
        icon-color="red"
        :is-open="confirmationModalIsOpen"
        :link-text="confirmationModal.confirm"
        :text-title="confirmationModal.title"
        :text="confirmationModal.text"
        :cancel="confirmationModal.cancel"
        @click="deleteRecord"
        @close="confirmationModalIsOpen = false"
      />
    </div>
    <div class="grid grid-cols-6 gap-2" :class="isRecording ? 'grid-cols-12' : 'grid-cols-6'">
      <div
        :class="
          isRecording
            ? 'col-span-12 translate-y-6'
            : 'col-span-6 xl:col-span-3  border rounded-md border-border translate-y-0 transition duration-300'
        "
      >
        <AppSoundRecorder @on-start-record="isRecording = true" @on-stop-record="handleUploadRecord" />
      </div>
      <div
        :class="
          isRecording
            ? 'col-span-12 opacity-0 h-0 translate-y-6'
            : 'col-span-6 xl:col-span-3 opacity-100 h-auto translate-y-0 transition duration-300'
        "
      >
        <AppSoundUploader :key="recordingName" @on-update="handleUploadRecord" />
      </div>
    </div>
  </div>
</template>
